import type { ResumeSettings } from '../types/resume';

export const DEFAULT_SETTINGS: ResumeSettings = {
  format: 'A4',
  margins: { top: 0.3, bottom: 0.3, left: 0.3, right: 0.3 },
  spacing: { sectionGap: 10, titleGap: 4, contentGap: 6, lineSpacing: 1.5 },
  fonts: { primary: 'Inter', secondary: 'Inter' },
  sizes: { heading: 100, body: 100 },
  dateFormat: 'short',
  headerAlignment: 'left',
  sectionOrder: ['summary', 'experience', 'education', 'skills', 'projects', 'certifications'],
};

export function mergeSettings(settings?: Partial<ResumeSettings>): ResumeSettings {
  return {
    ...DEFAULT_SETTINGS,
    ...settings,
    margins: { ...DEFAULT_SETTINGS.margins, ...settings?.margins },
    spacing: { ...DEFAULT_SETTINGS.spacing, ...settings?.spacing },
    fonts: { ...DEFAULT_SETTINGS.fonts, ...settings?.fonts },
    sizes: { ...DEFAULT_SETTINGS.sizes, ...settings?.sizes },
    sectionOrder: settings?.sectionOrder?.length ? settings.sectionOrder : DEFAULT_SETTINGS.sectionOrder,
  };
}

export function formatDate(dateStr: string, dateFormat: ResumeSettings['dateFormat']): string {
  if (!dateStr) return dateStr;
  const d = new Date(dateStr + '-01');
  if (isNaN(d.getTime())) return dateStr;
  if (dateFormat === 'short') return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  if (dateFormat === 'long') return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  return d.toLocaleDateString('en-US', { month: '2-digit', year: 'numeric' });
}

export function scale(baseRem: number, scale: number): string {
  return `${baseRem * (scale / 100)}rem`;
}

export function fontFamily(font: string, fallback = 'system-ui, sans-serif'): string {
  return `${font}, ${fallback}`;
}

export function pagePadding(margins: ResumeSettings['margins']): string {
  return `${margins.top}in ${margins.right}in ${margins.bottom}in ${margins.left}in`;
}

export type ResumeStyle = {
  '--scale-heading': string;
  '--scale-body': string;
  fontFamily: string;
  lineHeight: number;
  padding: string;
};

export function resumeStyleVars(settings: ResumeSettings): ResumeStyle {
  return {
    '--scale-heading': String(settings.sizes.heading / 100),
    '--scale-body': String(settings.sizes.body / 100),
    fontFamily: fontFamily(settings.fonts.primary),
    lineHeight: settings.spacing.lineSpacing,
    padding: pagePadding(settings.margins),
  } as ResumeStyle;
}
